import {
  getPricingSettings,
  setPricingSettings,
  variableDealerGetStrategy,
  variableDealerCreate,
  variableDealerUpdate,
  fixedByZipDealerGetStrategy,
  fixedByZipDealerCreate,
  fixedByZipDealerUpdateRow,
  fixedByZipDealerDeleteRow,
  pricingBandsDealerGetStrategy,
  pricingBandsDealerGetSettings,
  pricingBandsDealerSetSettings,
  pricingBandsDealerCreateRow,
  pricingBandsDealerUpdateRow,
  pricingBandsDealerDeleteRow,
  costPlusDealerGetStrategy,
  costPlusDealerCreate,
  costPlusDealerUpdate, // fixedByZipTransporterGetStrategy,
  // fixedByZipTransporterAddRow,
  // fixedByZipTransporterUpdateRow,
  // fixedByZipTransporterDeleteRow,
  residentialDealerGetStrategy,
  residentialDealerCreate,
  residentialDealerUpdate,
  bracketedMileageDealerGetStrategy,
  bracketedMileageDealerAddRow,
  bracketedMileageDealerUpdateRow,
  bracketedMileageDealerDeleteRow,
} from "@/services/pricingStrategies";
import { getVehicleBreakpointsByName } from "@/services/vehicles";
import { PricingStrategyType, PricingValueTypeEnum, VehicleBreakpointColumns } from "@/components/Strategies/constants";

const Model = {
  namespace: "pricingStrategies",
  state: {
    strategyId: null,
    settingsConcurrencyStamp: null,
    parentReferenceId: null,
    procedure: null,
    dealerStrategyInherited: false,
    dealerStrategyType: null,
    transporterStrategyType: null,
    transporterStrategyInherited: false,
    dealerStrategy: null,
    draftDealerStrategy: null,
    transporterStrategy: null,
    draftTransporterStrategy: null,

    dealerPricingBandsSettings: null,
    vehicleBreakpoints: [],
  },
  effects: {
    /* Dealers */
    *dealerGetStrategyVariable({ referenceId }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          variableDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
        );

        yield put({
          type: "setDealerStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperVariable,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyResidential({ referenceId }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          residentialDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
        );

        yield put({
          type: "setDealerStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperResidential,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyBracketedMileage({ referenceId, page, pageSize }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          bracketedMileageDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
          page,
          pageSize,
        );

        yield put({
          type: "setDealerStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperBracketedMileage,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerBracketedMileageAddRow({ referenceId, amount, distance, page, pageSize }, { call, put }) {
      try {
        const response = yield call(bracketedMileageDealerAddRow, referenceId, amount, distance);

        if (response === undefined) {
          return false;
        }

        yield put({
          type: "pricingStrategies/dealerGetStrategyBracketedMileage",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerBracketedMileageUpdateRow({ rowId, referenceId, amount, distance, page, pageSize }, { call, put }) {
      try {
        yield call(bracketedMileageDealerUpdateRow, rowId, referenceId, amount, distance);

        yield put({
          type: "pricingStrategies/dealerGetStrategyBracketedMileage",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerBracketedMileageDeleteRow({ referenceId, rowId, page, pageSize }, { call, put }) {
      try {
        yield call(bracketedMileageDealerDeleteRow, rowId);

        yield put({
          type: "pricingStrategies/dealerGetStrategyBracketedMileage",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyPricingBands({ referenceId, page, pageSize }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          pricingBandsDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
          page,
          pageSize,
        );

        yield put({
          type: "setDealerStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperPricingBand,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerPricingBandsGetSettings({ referenceId }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          pricingBandsDealerGetSettings,
          dealerStrategyInherited ? parentReferenceId : referenceId,
        );

        yield put({
          type: "setDealerPricingBandsSettings",
          payload: response,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerPricingBandsSetSettings({ id, concurrencyStamp, threshold, flatRate, pricePerMile }, { call, put }) {
      try {
        const response = yield call(
          pricingBandsDealerSetSettings,
          id,
          concurrencyStamp,
          threshold,
          flatRate,
          pricePerMile,
        );

        yield put({
          type: "setDealerPricingBandsSettings",
          payload: response,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerPricingBandsAddRow({ referenceId, distanceLower, distanceUpper, price, page, pageSize }, { call, put }) {
      try {
        const response = yield call(pricingBandsDealerCreateRow, referenceId, distanceLower, distanceUpper, price);

        if (response === undefined) {
          return false;
        }

        yield put({
          type: "pricingStrategies/dealerGetStrategyPricingBands",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerPricingBandsUpdateRow(
      { id, referenceId, concurrencyStamp, distanceLower, distanceUpper, price, page, pageSize },
      { call, put },
    ) {
      try {
        const response = yield call(
          pricingBandsDealerUpdateRow,
          id,
          concurrencyStamp,
          referenceId,
          distanceLower,
          distanceUpper,
          price,
        );

        if (response === undefined) {
          return false;
        }

        yield put({
          type: "pricingStrategies/dealerGetStrategyPricingBands",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerPricingBandsDeleteRow({ referenceId, rowId, page, pageSize }, { call, put }) {
      try {
        yield call(pricingBandsDealerDeleteRow, rowId);

        yield put({
          type: "pricingStrategies/dealerGetStrategyPricingBands",
          referenceId,
          page,
          pageSize,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyFixedByZip({ referenceId, page, pageSize }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          fixedByZipDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
          page,
          pageSize,
        );

        const vehicleBreakpoints = yield call(getVehicleBreakpointsByName, VehicleBreakpointColumns);
        const breakpointMap = new Map(vehicleBreakpoints?.map(bp => [bp.id, bp.name]));

        yield put({ type: "setVehicleBreakpoints", payload: vehicleBreakpoints || [] });

        const payload = {
          ...response,
          referenceId,
          strategyType: PricingStrategyType.ShipperFixed,
          items: response?.items?.map(item => ({
            ...item,
            vehicleBreakpointPrices: item?.vehicleBreakpointPrices?.map(bp => ({
              ...bp,
              name: breakpointMap.get(bp.breakpointId),
            })),
          })),
        };

        yield put({
          type: "setDealerStrategy",
          payload,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerFixedByZipAddRow(
      { referenceId, zipFrom, zipTo, direction, priceType, page, pageSize, vehicleBreakpointPrices },
      { call, put, select },
    ) {
      const { dealerStrategyInherited, parentReferenceId } = yield select(state => state.pricingStrategies);

      try {
        yield call(
          fixedByZipDealerCreate,
          dealerStrategyInherited ? parentReferenceId : referenceId,
          zipFrom,
          zipTo,
          priceType,
          direction,
          vehicleBreakpointPrices,
        );

        yield put({
          type: "pricingStrategies/dealerGetStrategyFixedByZip",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerFixedByZipUpdateRow(
      { referenceId, zipFrom, zipTo, id, priceType, concurrencyStamp, page, pageSize, vehicleBreakpointPrices },
      { call, put },
    ) {
      try {
        yield call(fixedByZipDealerUpdateRow, zipFrom, zipTo, priceType, id, concurrencyStamp, vehicleBreakpointPrices);

        yield put({
          type: "pricingStrategies/dealerGetStrategyFixedByZip",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerFixedByZipDeleteRow({ referenceId, rowId, page, pageSize }, { call, put }) {
      try {
        yield call(fixedByZipDealerDeleteRow, rowId);

        yield put({
          type: "pricingStrategies/dealerGetStrategyFixedByZip",
          referenceId,
          page,
          pageSize,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyCostPlus({ referenceId }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          costPlusDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
        );

        yield put({
          type: "setDealerStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperCostPlus,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *dealerGetStrategyNissanCityToCity({ referenceId }, { put }) {
      yield put({
        type: "setDealerStrategy",
        payload: {
          referenceId,
          concurrencyStamp: null,
          strategyType: PricingStrategyType.ShipperNissanCityToCity,
          id: null,
        },
      });
    },
    *dealerGetStrategyHistorical({ referenceId }, { put }) {
      yield put({
        type: "setDealerStrategy",
        payload: {
          referenceId,
          concurrencyStamp: null,
          strategyType: PricingStrategyType.ShipperHistorical,
          id: null,
        },
      });
    },
    *dealerSetDraftStrategy({ strategy }, { put }) {
      yield put({
        type: "setDealerDraftStrategy",
        strategyType: strategy.strategyType,
        payload: strategy,
      });
    },

    /* Transporter */
    *transporterGetStrategyVariable({ referenceId }, { call, put, select }) {
      const { parentReferenceId, dealerStrategyInherited } = yield select(state => ({
        parentReferenceId: state.pricingStrategies.parentReferenceId,
        dealerStrategyInherited: state.pricingStrategies.dealerStrategyInherited,
      }));

      try {
        const response = yield call(
          variableDealerGetStrategy,
          dealerStrategyInherited ? parentReferenceId : referenceId,
        );

        yield put({
          type: "setTransporterStrategy",
          payload: {
            ...response,
            referenceId,
            strategyType: PricingStrategyType.ShipperVariable,
          },
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *transporterGetVariable({ referenceId }, { put }) {
      yield put({
        type: "setTransporterStrategy",
        payload: {
          referenceId,
          concurrencyStamp: null,
          strategyType: PricingStrategyType.TransporterVariable,
          id: null,
        },
      });
    },
    *transporterGetStrategyNissanCityToCity({ referenceId }, { put }) {
      yield put({
        type: "setTransporterStrategy",
        payload: {
          referenceId,
          concurrencyStamp: null,
          strategyType: PricingStrategyType.TransporterNissanCityToCity,
          id: null,
        },
      });
    },
    *transporterGetStrategyHistorical({ referenceId }, { put }) {
      yield put({
        type: "setTransporterStrategy",
        payload: {
          referenceId,
          concurrencyStamp: null,
          strategyType: PricingStrategyType.TransporterHistorical,
          id: null,
        },
      });
    },

    *transporterSetDraftStrategy({ strategy }, { put }) {
      yield put({
        type: "setTransporterDraftStrategy",
        referenceId: strategy.referenceId,
        strategyType: strategy.strategyType,
        payload: strategy,
      });
    },

    /* Both */
    *getSettings({ referenceId }, { call, put }) {
      try {
        const response = yield call(getPricingSettings, referenceId);

        yield put({
          type: "storeSettingsValues",
          payload: response,
        });

        return response;
      } catch (error) {
        return null;
      }
    },
    *setSettings(
      { strategyId, settingsConcurrencyStamp, procedure, dealerStrategyType, transporterStrategyType },
      { call, put },
    ) {
      try {
        const response = yield call(
          setPricingSettings,
          strategyId,
          settingsConcurrencyStamp,
          procedure,
          dealerStrategyType,
          transporterStrategyType,
        );

        yield put({
          type: "storeSettingsValues",
          payload: response,
        });

        return true;
      } catch (error) {
        return false;
      }
    },
    *submitStrategies({ dealerStrategy, transporterStrategy }, { call, put }) {
      let dealerResponse = null;
      let dealerError = null;
      let transporterResponse = null;
      let transporterError = null;

      const dealerIsDefault = dealerStrategy?.valueType === PricingValueTypeEnum.Default;
      const transporterIsDefault = transporterStrategy?.valueType === PricingValueTypeEnum.Default;

      if (dealerStrategy !== null) {
        switch (dealerStrategy.strategyType) {
          case PricingStrategyType.ShipperVariable: {
            try {
              dealerResponse = yield call(
                dealerIsDefault ? variableDealerCreate : variableDealerUpdate,
                dealerIsDefault ? dealerStrategy.referenceId : dealerStrategy.concurrencyStamp,
                dealerStrategy.margin,
                dealerStrategy.minimum,
                dealerStrategy.id,
              );
            } catch (error) {
              dealerError = error;
            }

            break;
          }
          case PricingStrategyType.ShipperCostPlus: {
            try {
              dealerResponse = yield call(
                dealerIsDefault ? costPlusDealerCreate : costPlusDealerUpdate,
                dealerIsDefault ? dealerStrategy.referenceId : dealerStrategy.concurrencyStamp,
                dealerStrategy.amount,
                dealerStrategy.id,
              );
            } catch (error) {
              dealerError = error;
            }
            break;
          }
          case PricingStrategyType.ShipperResidential: {
            try {
              dealerResponse = yield call(
                dealerIsDefault ? residentialDealerCreate : residentialDealerUpdate,
                dealerIsDefault ? dealerStrategy.referenceId : dealerStrategy.concurrencyStamp,
                dealerStrategy.margin,
                dealerStrategy.id,
              );
            } catch (error) {
              dealerError = error;
            }

            break;
          }

          default:
            break;
        }
      }

      if (transporterStrategy !== null) {
        switch (transporterStrategy.strategyType) {
          case PricingStrategyType.ShipperVariable: {
            try {
              transporterResponse = yield call(
                transporterIsDefault ? variableDealerCreate : variableDealerUpdate,
                transporterIsDefault ? transporterStrategy.referenceId : transporterStrategy.concurrencyStamp,
                transporterStrategy.margin,
                transporterStrategy.minimum,
                transporterStrategy.id,
              );
            } catch (error) {
              transporterError = error;
            }

            break;
          }

          default:
            break;
        }
      }

      if (dealerResponse !== null) {
        const payload = {
          ...dealerResponse,
          strategyType: dealerStrategy.strategyType,
          valueType: dealerResponse.type,
        };

        delete payload.type;

        yield put({
          type: "setDealerStrategy",
          payload,
        });
      }

      if (transporterResponse !== null) {
        const payload = {
          ...transporterResponse,
          strategyType: transporterStrategy.strategyType,
          valueType: transporterResponse.type,
        };

        delete payload.type;

        yield put({
          type: "setTransporterStrategy",
          payload,
        });
      }

      return {
        dealerError,
        transporterError,
      };
    },
  },
  reducers: {
    storeSettingsValues(state, { payload }) {
      const {
        id: strategyId,
        concurrencyStamp,
        procedure,
        transporterStrategy,
        shipperStrategy,
        isShipperOverwritten,
        isTransporterOverwritten,
        parentReferenceId,
      } = payload;

      return {
        ...state,
        strategyId,
        settingsConcurrencyStamp: concurrencyStamp,
        parentReferenceId,
        procedure,
        dealerStrategyType: shipperStrategy,
        dealerStrategyInherited: !isShipperOverwritten && parentReferenceId !== null,
        transporterStrategyType: transporterStrategy,
        transporterStrategyInherited: !isTransporterOverwritten && parentReferenceId !== null,
      };
    },
    setDealerStrategy(state, { payload }) {
      const newStrategy = {
        ...payload,
        valueType: payload?.type,
      };

      delete newStrategy.type;

      return {
        ...state,
        dealerStrategy: newStrategy,
        draftDealerStrategy: newStrategy,
      };
    },
    setVehicleBreakpoints(state, { payload }) {
      return { ...state, vehicleBreakpoints: payload };
    },
    setDealerPricingBandsSettings(state, { payload }) {
      return {
        ...state,
        dealerPricingBandsSettings: {
          id: payload.id,
          concurrencyStamp: payload.concurrencyStamp,
          threshold: payload.threshold,
          flatRate: payload.flatRate,
          pricePerMile: payload.pricePerMile,
        },
      };
    },
    setTransporterStrategy(state, { payload }) {
      const newStrategy = {
        ...payload,
        valueType: payload?.type,
      };

      delete newStrategy.type;

      return {
        ...state,
        transporterStrategy: newStrategy,
        draftTransporterStrategy: newStrategy,
      };
    },
    setDealerDraftStrategy(state, { strategyType, payload }) {
      const draftDealerStrategy = {
        strategyType,
        concurrencyStamp: payload?.concurrencyStamp,
        id: payload?.id,
        referenceId: payload?.referenceId,
        valueType: payload?.valueType,
      };

      switch (payload.strategyType) {
        case PricingStrategyType.ShipperVariable:
          if (payload.margin) {
            draftDealerStrategy.margin = payload.margin;
          }
          if (payload.minimum) {
            draftDealerStrategy.minimum = payload.minimum;
          }
          break;

        case PricingStrategyType.ShipperResidential:
          draftDealerStrategy.margin = payload.margin;
          break;

        case PricingStrategyType.ShipperCostPlus:
          draftDealerStrategy.amount = payload.amount;
          break;

        default:
          break;
      }

      return {
        ...state,
        draftDealerStrategy,
      };
    },
    setTransporterDraftStrategy(state, { payload }) {
      const draftTransporterStrategy = {
        strategyType: payload.strategyType,
        concurrencyStamp: payload.concurrencyStamp,
        id: payload.id,
        referenceId: payload.referenceId,
        valueType: payload?.valueType,
      };

      switch (payload.strategyType) {
        case PricingStrategyType.ShipperVariable:
          if (payload.margin) {
            draftTransporterStrategy.margin = payload.margin;
          }
          if (payload.minimum) {
            draftTransporterStrategy.minimum = payload.minimum;
          }
          break;

        default:
          break;
      }

      return {
        ...state,
        draftTransporterStrategy,
      };
    },
    discardDealerDraftStrategy(state) {
      return {
        ...state,
        draftDealerStrategy: state.dealerStrategy,
        draftTransporterStrategy: state.transporterStrategy,
      };
    },
    discardDraftTransporterStrategy(state) {
      return {
        ...state,
        draftDealerStrategy: state.dealerStrategy,
        draftTransporterStrategy: state.transporterStrategy,
      };
    },
  },
};

export default Model;
